import { View, Text, Button, CircularIcon, SimpleIcon } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import { useUnistyles } from "react-native-unistyles";
import { memo, type FC } from "react";

export interface ArmStatusCardProps {
  readonly isArmed: boolean;
  readonly statusText: string;
  readonly scheduleText?: string;
  readonly onToggle: () => void;
  readonly onAdjust?: () => void;
  readonly isLoading?: boolean;
  readonly disabled?: boolean;
}

export const ArmStatusCard: FC<ArmStatusCardProps> = memo(
  ({ isArmed, statusText, scheduleText, onToggle, onAdjust, isLoading = false, disabled = false }) => {
    const { theme } = useUnistyles();

    const iconColor = isArmed ? theme.colors.alarmStates.armed : theme.colors.warning[500];
    const iconBorderColor = isArmed ? theme.colors.alarmStates.armedBorder : theme.colors.alarmStates.disarmedBorder;
    const iconBackground = isArmed ? theme.colors.alarmStates.armed : theme.colors.surfaceCard;

    return (
      <View
        flex={1}
        minHeight={202}
        bg="surfaceCard"
        borderRadius={theme.spacing.xl}
        borderWidth={1}
        borderColor="borderCard"
        p="5xl"
        gap="3xl"
      >
        <Text textStyle="sm/semibold" color="textMuted">
          Arm status
        </Text>

        {/* Armed Status Row */}
        <View
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          height={theme.spacing["12xl"]}
          bg="surfaceCardInner"
          borderRadius={theme.spacing.lg}
          borderWidth={1}
          borderColor="borderCard"
          px="3xl"
          py="lg"
          gap="xl"
        >
          <View flex={1} flexDirection="row" alignItems="center" gap="xl" style={{ minWidth: 0 }}>
            <View
              width={theme.spacing["8xl"]}
              height={theme.spacing["8xl"]}
              alignItems="center"
              justifyContent="center"
            >
              <CircularIcon
                name={isArmed ? "lockClosed" : "lockOpen"}
                backgroundColor={iconBackground}
                borderColor={iconBorderColor}
                iconColor={isArmed ? theme.colors.white.default : iconColor}
              />
            </View>
            <View>
              <Text textStyle="md/regular" color="text">
                {isArmed ? "Armed" : "Disarmed"}
              </Text>
              <Text textStyle="xxs/regular" color="textMuted">
                {statusText}
              </Text>
            </View>
          </View>
          <Button
            label={isArmed ? "Disarm" : "Arm"}
            onPress={onToggle}
            variant="primary"
            variantColor="blue"
            loading={isLoading}
            size="md"
            disabled={disabled}
          />
        </View>

        {/* Schedule/Adjust Row */}
        <View
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          height={theme.spacing["12xl"]}
          bg="surfaceCardInner"
          borderRadius={theme.spacing.lg}
          borderWidth={1}
          borderColor="borderCard"
          px="3xl"
          py="lg"
          gap="xl"
        >
          <View flex={1} flexDirection="row" alignItems="center" gap="xl" style={{ minWidth: 0 }}>
            <View
              width={theme.spacing["8xl"]}
              height={theme.spacing["8xl"]}
              alignItems="center"
              justifyContent="center"
            >
              <SimpleIcon name="clockOutline" />
            </View>
            <View>
              <Text textStyle="xs/regular" color="text">
                {scheduleText || "No upcoming changes"}
              </Text>
              <Text textStyle="xxs/regular" color="textMuted">
                {statusText}
              </Text>
            </View>
          </View>
          {onAdjust && (
            <Button
              label="Adjust"
              onPress={onAdjust}
              variant="secondary"
              variantColor="grey"
              size="md"
              disabled={disabled}
            />
          )}
        </View>
      </View>
    );
  },
);
